import { ElementApi, KEYS, NodeApi, PathApi, RangeApi, createTSlatePlugin, isDefined, isUrl, sanitizeUrl } from "platejs";

//#region src/lib/utils/createLinkNode.ts
const createLinkNode = (editor, { children, target, text = "", url }) => {
	const type = editor.getType(KEYS.link);
	return {
		children: children ?? [{ text }],
		target,
		type,
		url
	};
};

//#endregion
//#region src/lib/utils/encodeUrlIfNeeded.ts
/**
* If a URL has not already been encoded and contains characters that require
* encoding, encode the URL.
*
* @param url URL to encode
*/
const encodeUrlIfNeeded = (url) => {
	try {
		return url !== decodeURIComponent(url) ? url : encodeURI(url);
	} catch (error) {
		if (error instanceof URIError) return url;
		throw error;
	}
};

//#endregion
//#region src/lib/utils/getLinkAttributes.ts
const getLinkAttributes = (editor, link) => {
	const { allowedSchemes, dangerouslySkipSanitization, defaultLinkAttributes } = editor.getOptions({ key: KEYS.link });
	const attributes = { ...defaultLinkAttributes };
	const href = dangerouslySkipSanitization ? link.url : sanitizeUrl(link.url, { allowedSchemes }) || void 0;
	if (href !== void 0) attributes.href = href;
	if ("target" in link && link.target !== void 0) attributes.target = link.target;
	return attributes;
};

//#endregion
//#region src/lib/utils/safeDecodeUrl.ts
const safeDecodeUrl = (url) => {
	try {
		return decodeURI(url);
	} catch (error) {
		if (error instanceof URIError) return url;
		throw error;
	}
};

//#endregion
//#region src/lib/utils/validateUrl.ts
const MARKDOWN_HEADING_PATTERN = /^#{1,6}\s+/;
const validateUrl = (editor, url) => {
	const { allowedSchemes, dangerouslySkipSanitization, isUrl: isUrl$1 } = editor.getOptions(BaseLinkPlugin);
	if (url.startsWith("/")) return true;
	if (url.startsWith("#")) {
		if (MARKDOWN_HEADING_PATTERN.test(url)) return false;
		return true;
	}
	if (isUrl$1 && !isUrl$1(url)) return false;
	if (!dangerouslySkipSanitization && !sanitizeUrl(url, {
		allowedSchemes,
		permitInvalid: true
	})) return false;
	return true;
};

//#endregion
//#region src/lib/transforms/insertLink.ts
/** Insert a link node. */
const insertLink = (editor, createLinkNodeOptions, options) => {
	editor.tf.insertNodes([createLinkNode(editor, createLinkNodeOptions)], options);
};

//#endregion
//#region src/lib/transforms/unwrapLink.ts
/** Unwrap link node. */
const unwrapLink = (editor, options) => {
	return editor.tf.withoutNormalizing(() => {
		if (options?.split) {
			if (editor.api.above({
				at: editor.selection?.anchor,
				match: { type: editor.getType(KEYS.link) }
			})) {
				editor.tf.splitNodes({
					at: editor.selection?.anchor,
					match: (n) => ElementApi.isElement(n) && n.type === editor.getType(KEYS.link)
				});
				unwrapLink(editor, { at: editor.selection?.anchor });
				return true;
			}
			if (editor.api.above({
				at: editor.selection?.focus,
				match: { type: editor.getType(KEYS.link) }
			})) {
				editor.tf.splitNodes({
					at: editor.selection?.focus,
					match: (n) => ElementApi.isElement(n) && n.type === editor.getType(KEYS.link)
				});
				unwrapLink(editor, { at: editor.selection?.focus });
				return true;
			}
		}
		editor.tf.unwrapNodes({
			match: { type: editor.getType(KEYS.link) },
			...options
		});
	});
};

//#endregion
//#region src/lib/transforms/upsertLinkText.ts
/**
* If the text is different than the link above text, replace link children by a
* new text. The new text has the same marks than the first text replaced.
*/
const upsertLinkText = (editor, { text }) => {
	const newLink = editor.api.above({ match: { type: editor.getType(KEYS.link) } });
	if (newLink) {
		const [newLinkNode, newLinkPath] = newLink;
		if (text?.length && text !== editor.api.string(newLinkPath)) {
			const firstText = newLinkNode.children[0];
			editor.tf.replaceNodes({
				...firstText,
				text
			}, {
				at: newLinkPath,
				children: true,
				select: true
			});
		}
	}
};

//#endregion
//#region src/lib/transforms/wrapLink.ts
/** Wrap a link node with split. */
const wrapLink = (editor, { target, url, ...options }) => {
	editor.tf.wrapNodes({
		children: [],
		target,
		type: editor.getType(KEYS.link),
		url
	}, {
		split: true,
		...options
	});
};

//#endregion
//#region src/lib/transforms/upsertLink.ts
/**
* If selection in a link or is not url:
*
* - Insert text with url, exit If selection is expanded or `update` in a link:
* - Remove link node, get link text Then:
* - Insert link node
*/
const upsertLink = (editor, { insertNodesOptions, insertTextInLink, skipValidation = false, target, text, url }) => {
	const at = editor.selection;
	if (!at) return;
	const linkAbove = editor.api.above({
		at,
		match: { type: editor.getType(KEYS.link) }
	});
	if (insertTextInLink && linkAbove) {
		editor.tf.insertText(url);
		return true;
	}
	if (!skipValidation && !validateUrl(editor, url)) return;
	if (isDefined(text) && text.length === 0) text = url;
	if (linkAbove) {
		if (url !== linkAbove[0]?.url || target !== linkAbove[0]?.target) editor.tf.setNodes({
			target,
			url
		}, { at: linkAbove[1] });
		upsertLinkText(editor, {
			target,
			text,
			url
		});
		return true;
	}
	const [linkNode, linkPath] = editor.api.node({
		at,
		match: { type: editor.getType(KEYS.link) }
	}) ?? [];
	let shouldReplaceText = false;
	if (linkPath && text?.length) {
		const linkText = editor.api.string(linkPath);
		if (text !== linkText) shouldReplaceText = true;
	}
	if (RangeApi.isExpanded(at)) {
		if (linkAbove) unwrapLink(editor, { at: linkAbove[1] });
		else unwrapLink(editor, { split: true });
		wrapLink(editor, {
			target,
			url
		});
		upsertLinkText(editor, {
			target,
			text,
			url
		});
		return true;
	}
	if (shouldReplaceText) editor.tf.removeNodes({ at: linkPath });
	const props = NodeApi.extractProps(linkNode ?? {});
	const path = editor.selection?.focus.path;
	if (!path) return;
	const leaf = NodeApi.leaf(editor, path);
	if (!text?.length) text = url;
	insertLink(editor, {
		...props,
		children: [{
			...leaf,
			text
		}],
		target,
		url
	}, insertNodesOptions);
	return true;
};

//#endregion
//#region src/lib/withLink.ts
/**
* Insert space after a url to wrap a link. Lookup from the block start to the
* cursor to check if there is an url. If not found, lookup before the cursor
* for a space character to check the url.
*
* On insert data: Paste a string inside a link element will edit its children
* text but not its url.
*/
const withLink = ({ editor, getOptions, tf: { insertBreak, insertData, insertText, normalizeNode }, type }) => {
	const wrapLink$1 = () => {
		const { getUrlHref, isUrl: isUrl$1, rangeBeforeOptions } = getOptions();
		editor.tf.withoutNormalizing(() => {
			const selection = editor.selection;
			let beforeWordRange = editor.api.range("before", selection, { before: rangeBeforeOptions });
			if (!beforeWordRange) beforeWordRange = editor.api.range("start", editor.selection);
			if (!beforeWordRange) return;
			if (editor.api.some({
				at: beforeWordRange,
				match: { type }
			})) return;
			let beforeWordText = editor.api.string(beforeWordRange);
			beforeWordText = getUrlHref?.(beforeWordText) ?? beforeWordText;
			if (!isUrl$1(beforeWordText)) return;
			editor.tf.select(beforeWordRange);
			upsertLink(editor, { url: beforeWordText });
			editor.tf.collapse({ edge: "end" });
		});
	};
	return { transforms: {
		insertBreak() {
			if (!editor.api.isCollapsed()) return insertBreak();
			wrapLink$1();
			insertBreak();
		},
		insertData(data) {
			const { getUrlHref, keepSelectedTextOnPaste } = getOptions();
			const text = data.getData("text/plain");
			const textHref = getUrlHref?.(text);
			if (text) {
				const value = textHref || text;
				if (upsertLink(editor, {
					insertTextInLink: true,
					text: keepSelectedTextOnPaste ? void 0 : value,
					url: value
				})) return;
			}
			insertData(data);
		},
		insertText(text, options) {
			if (text === " " && editor.api.isCollapsed()) wrapLink$1();
			insertText(text, options);
		},
		normalizeNode([node, path]) {
			if (node.type === type) {
				const range = editor.selection;
				if (range && editor.api.isCollapsed() && editor.api.isEnd(range.focus, path)) {
					if (!editor.api.start(path, { next: true })) {
						const nextPath = PathApi.next(path);
						editor.tf.insertNodes({ text: "" }, { at: nextPath });
						editor.tf.select(nextPath);
					}
				}
			}
			normalizeNode([node, path]);
		}
	} };
};

//#endregion
//#region src/lib/BaseLinkPlugin.ts
/** Enables support for hyperlinks. */
const BaseLinkPlugin = createTSlatePlugin({
	key: KEYS.link,
	node: {
		dangerouslyAllowAttributes: ["target"],
		isElement: true,
		isInline: true,
		props: ({ editor, element }) => getLinkAttributes(editor, element)
	},
	options: {
		allowedSchemes: [
			"http",
			"https",
			"mailto",
			"tel"
		],
		dangerouslySkipSanitization: false,
		defaultLinkAttributes: {},
		isUrl,
		keepSelectedTextOnPaste: true,
		rangeBeforeOptions: {
			afterMatch: true,
			matchBlockStart: true,
			matchString: " ",
			skipInvalid: true
		}
	},
	parsers: { html: { deserializer: {
		rules: [{ validNodeName: "A" }],
		parse: ({ editor, element, type }) => {
			const url = element.getAttribute("href");
			if (url && validateUrl(editor, url)) return {
				target: element.getAttribute("target") || "_blank",
				type,
				url
			};
		}
	} } },
	rules: {
		normalize: { removeEmpty: true },
		selection: { affinity: "directional" }
	}
}).overrideEditor(withLink);

//#endregion
export { upsertLinkText as a, validateUrl as c, encodeUrlIfNeeded as d, createLinkNode as f, wrapLink as i, safeDecodeUrl as l, withLink as n, unwrapLink as o, upsertLink as r, insertLink as s, BaseLinkPlugin as t, getLinkAttributes as u };